/**
 * The one brand map (P25009-478 BR-2): a stored tyre/rim brand value turned
 * into the name a customer reads.
 *
 * `publicData.brand` holds the Console "Däckmärke" option KEY, not a name.
 * Several keys are camelCase joins ("kumhoTire", "nokianTyres") and every one
 * of them has reached a customer verbatim at some point — in a wizard title,
 * in an AI description ("från KumhoTire"), on an imported listing. This file
 * is the single place that knows how each key is spelled out; the browser
 * (src/util/brandLabel.js), the API server (server/api-util/brandLabel.js)
 * and the importer (resolveBrand) all read it, so they can no longer drift
 * apart the way the old twins did ("Toko Tires" on the server, "BFGoodrich"
 * in the browser).
 *
 * The labels are the LIVE Console labels. Test spells a few the long way
 * ("Kumho Tire") — the page prefers whichever label the marketplace's own
 * enum gives (brandDisplayName), so this map decides stored text: titles,
 * descriptions, feeds, PDFs.
 *
 * Written as CommonJS on purpose, like boltCircleCore.js: shared by the
 * browser bundle and the plain-Node API server, which is not transpiled.
 */

/* Console option key -> display name. Every key MUST be an option value of
 * the Console brand field on BOTH marketplaces, spelled exactly as Console
 * spells it — the supplier mappers treat this list as "the marketplace has
 * this option". The P25009-482 keys are lower case because Live already had
 * them that way. */
const BRAND_LABELS = {
  bfGoodrich: 'BF Goodrich',
  bridgestone: 'Bridgestone',
  continental: 'Continental',
  cooperTire: 'Cooper Tire',
  dunlop: 'Dunlop',
  falken: 'Falken',
  generalTire: 'General Tire',
  goodyear: 'Goodyear',
  gtradial: 'GT Radial',
  hankook: 'Hankook',
  kumhoTire: 'Kumho',
  maxxis: 'Maxxis',
  michelin: 'Michelin',
  nexen: 'Nexen',
  nokianTyres: 'Nokian',
  nordman: 'Nordman',
  pirelli: 'Pirelli',
  roadx: 'RoadX',
  sailun: 'Sailun',
  // The key is misspelled in Console ("toko"); the brand is Toyo.
  tokoTires: 'Toyo',
  yokohama: 'Yokohama',
  vredestein: 'Vredestein',
};

/* Other names a file, a supplier or a seller writes for the same brand. Only
 * used to RESOLVE text to a key (resolveBrand); never printed. */
const BRAND_ALIASES = {
  bfGoodrich: ['BFGoodrich'],
  kumhoTire: ['Kumho Tire', 'Kumho Tyres'],
  nokianTyres: ['Nokian Tyres', 'Nokian Tires'],
  tokoTires: ['Toyo Tires', 'Toyo Tyres', 'Toko Tires'],
  roadx: ['Road X'],
};

/* Values that mean "no brand was given". `other` is the enum's catch-all —
 * roughly four out of five listings sit on it — and `annat`/`övrigt` are its
 * Swedish labels. None is a manufacturer, and none may reach a customer. */
const PLACEHOLDER_BRANDS = [
  'other',
  'annat',
  'övrigt',
  'ovrigt',
  'unknown',
  'okänt',
  'okant',
  'none',
];

const LABEL_BY_LOWER_KEY = Object.keys(BRAND_LABELS).reduce((acc, key) => {
  acc[key.toLowerCase()] = BRAND_LABELS[key];
  return acc;
}, {});

const upperFirstWords = value =>
  value
    .split(/\s+/)
    .map(word => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');

/** True for a stored value that names no manufacturer. */
const isPlaceholderBrand = value =>
  PLACEHOLDER_BRANDS.includes(
    String(value == null ? '' : value)
      .trim()
      .toLowerCase()
  );

/**
 * @param {String} value publicData.brand (a Console key), or free text
 * @return {String} a manufacturer name safe to publish, or '' when the value
 *   does not really name one. Callers must treat '' as "say nothing about the
 *   brand" — never print a stand-in, and never fall back to the car's make.
 */
const brandLabel = value => {
  const raw = String(value == null ? '' : value).trim();
  if (!raw || isPlaceholderBrand(raw)) return '';
  // Looked up without regard to case: Live and Test were set up apart and
  // the same brand must read the same whichever way its key was spelled.
  const known = LABEL_BY_LOWER_KEY[raw.toLowerCase()];
  if (known) return known;
  // A key this map does not know yet, or free text a seller typed ("Brabus"):
  // camelCase -> words, each capitalised. "rotiformWheels" -> "Rotiform Wheels".
  return upperFirstWords(raw.replace(/([a-zà-ÿ0-9])([A-ZÀ-Ý])/g, '$1 $2'));
};

/* Raw keys as they turn up inside generated text. The joined keys are only
 * ever machine spellings, whatever their case ("kumhoTire", "KumhoTire",
 * "nokiantyres"). Two joins are also the brand's own trademark spelling, so
 * those exact spellings are left alone. "Toko Tires" is what the old server
 * label printed for tokoTires — a name no customer should see either.
 * Single-word keys ("michelin") are not touched: a lower-case brand in
 * running text is a style choice, not a leaked key. */
const JOINED_KEYS = Object.keys(BRAND_LABELS).filter(
  key => /[a-z][A-Z]/.test(key) || BRAND_LABELS[key].includes(' ') || /[a-z][A-Z]/.test(BRAND_LABELS[key])
);
const PROPER_JOINED_SPELLINGS = ['BFGoodrich', 'BFGOODRICH', 'RoadX', 'ROADX'];
/* No lookbehind: this module ships in the browser bundle, and an older Safari
 * fails to parse a lookbehind — the whole bundle with it. The character in
 * front of the key is captured and put back instead. */
const RAW_KEY_PATTERN = new RegExp(
  `(^|[^\\p{L}\\p{N}_])(${JOINED_KEYS.join('|')}|toko\\s+tires?)(?![\\p{L}\\p{N}_])`,
  'giu'
);

/**
 * Replace every raw brand key inside a piece of text with the brand's name:
 * "Nytt Vinter dubbat 16\" kumhoTire" -> "Nytt Vinter dubbat 16\" Kumho".
 * The last line of defence for text a machine wrote (an AI echoing its input,
 * an old wizard title) and the rule the P25009-478 backfill applies.
 *
 * @param {String} text
 * @return {String} the text with each raw key replaced; unchanged otherwise
 */
const replaceRawBrandKeys = text => {
  const source = String(text == null ? '' : text);
  if (!source) return source;
  return source.replace(RAW_KEY_PATTERN, (whole, before, match) => {
    if (PROPER_JOINED_SPELLINGS.includes(match)) return whole;
    if (/^toko\s+tires?$/i.test(match)) return `${before}${BRAND_LABELS.tokoTires}`;
    return `${before}${LABEL_BY_LOWER_KEY[match.toLowerCase()] || match}`;
  });
};

/** True when `replaceRawBrandKeys` would change the text. */
const hasRawBrandKey = text => replaceRawBrandKeys(text) !== String(text == null ? '' : text);

/**
 * Every spelling that names a brand key: the key, its label and its aliases.
 * The importer matches file text against these (after its own normalising),
 * so "Kumho", "Kumho Tire" and "kumhoTire" all land on `kumhoTire`.
 *
 * @return {Array<{ key: String, names: Array<String> }>}
 */
const brandNames = () =>
  Object.keys(BRAND_LABELS).map(key => ({
    key,
    names: [key, BRAND_LABELS[key], ...(BRAND_ALIASES[key] || [])],
  }));

module.exports = {
  BRAND_LABELS,
  PLACEHOLDER_BRANDS,
  brandLabel,
  brandNames,
  hasRawBrandKey,
  isPlaceholderBrand,
  replaceRawBrandKeys,
};
